/* eslint-disable prettier/prettier */
import { EMAIL_CONTENT } from '../../theme/tokens'

/**
 * Block registry for the in-house email builder.
 *
 * A design document is:
 *   { version: 'bhanzu-v1',
 *     body: { backgroundColor, contentBackground, contentWidth },
 *     blocks: [{ id, type, props }] }
 *
 * Each block type declares its default props and how it serializes to MJML.
 * Personalization variables ({{name}} etc.) pass through untouched.
 */

let counter = 0
export const newBlockId = () => {
    counter += 1
    return `blk_${Date.now().toString(36)}_${counter}`
}

const esc = (s) =>
    String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')

// Text props allow a small set of inline tags typed by the user (<b>, <a>…);
// they are passed through as-is, matching how the templates behave today.
const raw = (s) => String(s ?? '')

// A message typed over several lines is written with <br/> where the lines
// break: HTML (and so the email) would otherwise fold the newlines into one
// paragraph. Text that lays itself out with block tags is left as typed.
const BLOCK_TAGS = /<(p|div|br|table|ul|ol|li|h[1-6])[\s/>]/i
const withLineBreaks = (s) => {
    const text = raw(s)
    return BLOCK_TAGS.test(text) ? text : text.replace(/\r?\n/g, '<br/>')
}

export const BODY_DEFAULTS = {
    backgroundColor: EMAIL_CONTENT.backdrop,
    contentBackground: EMAIL_CONTENT.canvas,
    contentWidth: 600,
}

export const BLOCK_TYPES = {
    heading: {
        label: 'Heading',
        defaults: {
            text: 'Heading text',
            level: 'h1', // h1 | h2 | h3
            color: EMAIL_CONTENT.heading,
            align: 'left',
            paddingY: 10,
        },
        toMjml: (p) => {
            const size = { h1: 28, h2: 22, h3: 18 }[p.level] ?? 28
            return `<mj-text align="${p.align}" color="${
                p.color
            }" font-size="${size}px" font-weight="700" padding="${
                p.paddingY
            }px 25px">${raw(p.text)}</mj-text>`
        },
    },
    paragraph: {
        label: 'Paragraph',
        defaults: {
            // No variable: the form lists every variable in the content, so a
            // sample one was listed (and sent to the AI) though no one added it.
            text: 'Write your message here.',
            color: EMAIL_CONTENT.text,
            align: 'left',
            fontSize: EMAIL_CONTENT.textSize,
            lineHeight: 1.6,
            paddingY: 8,
        },
        toMjml: (p) =>
            `<mj-text align="${p.align}" color="${p.color}" font-size="${
                p.fontSize
            }px" line-height="${p.lineHeight}" padding="${
                p.paddingY
            }px 25px">${withLineBreaks(p.text)}</mj-text>`,
    },
    image: {
        label: 'Image',
        defaults: {
            src: '',
            alt: '',
            href: '',
            width: 560,
            align: 'center',
            paddingY: 10,
        },
        toMjml: (p) =>
            `<mj-image src="${esc(p.src)}" alt="${esc(p.alt)}"${
                p.href ? ` href="${esc(p.href)}"` : ''
            } width="${p.width}px" align="${p.align}" padding="${
                p.paddingY
            }px 25px" />`,
    },
    button: {
        label: 'Button',
        defaults: {
            text: 'Book a class',
            href: 'https://www.bhanzu.com',
            background: EMAIL_CONTENT.buttonBg,
            // Deep Blue on orange (4.5:1); white was 3.3:1.
            color: EMAIL_CONTENT.buttonText,
            align: 'center',
            borderRadius: EMAIL_CONTENT.buttonRadius,
            paddingY: 12,
        },
        toMjml: (p) =>
            `<mj-button href="${esc(p.href)}" background-color="${
                p.background
            }" color="${p.color}" align="${p.align}" border-radius="${
                p.borderRadius
            }px" font-weight="600" inner-padding="12px 28px" padding="${
                p.paddingY
            }px 25px">${raw(p.text)}</mj-button>`,
    },
    divider: {
        label: 'Divider',
        defaults: { color: EMAIL_CONTENT.divider, paddingY: 10 },
        toMjml: (p) =>
            `<mj-divider border-color="${p.color}" border-width="1px" padding="${p.paddingY}px 25px" />`,
    },
    spacer: {
        label: 'Spacer',
        defaults: { height: 24 },
        toMjml: (p) => `<mj-spacer height="${p.height}px" />`,
    },
    columns: {
        label: '2 Columns',
        defaults: {
            leftText: 'Left column text',
            rightText: 'Right column text',
            color: EMAIL_CONTENT.text,
            fontSize: EMAIL_CONTENT.textSize,
            paddingY: 8,
        },
        // Rendered as its own two-column section by the serializer.
        toColumns: (p) => [
            `<mj-text color="${p.color}" font-size="${p.fontSize}px" padding="${
                p.paddingY
            }px 15px">${raw(p.leftText)}</mj-text>`,
            `<mj-text color="${p.color}" font-size="${p.fontSize}px" padding="${
                p.paddingY
            }px 15px">${raw(p.rightText)}</mj-text>`,
        ],
        toMjml: () => '',
    },
    rawHtml: {
        label: 'HTML',
        defaults: { html: '<p style="margin:0">Raw HTML block</p>' },
        toMjml: (p) => `<mj-raw>${raw(p.html)}</mj-raw>`,
    },
}

export const PALETTE_ORDER = [
    'heading',
    'paragraph',
    'image',
    'button',
    'divider',
    'spacer',
    'columns',
    'rawHtml',
]

export const makeBlock = (type) => ({
    id: newBlockId(),
    type,
    props: { ...BLOCK_TYPES[type].defaults },
})

export const emptyDesign = () => ({
    version: 'bhanzu-v1',
    body: { ...BODY_DEFAULTS },
    blocks: [makeBlock('heading'), makeBlock('paragraph'), makeBlock('button')],
})

/** An AI email's plain text as the builder's inline HTML: escaped, **bold** as <b>. */
const aiText = (s) =>
    esc(s)
        .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
        .replace(/\r?\n/g, '<br/>')

/** A label shown in bold: any **bold** markers in it are dropped, so it is not bold twice. */
const boldText = (s) => `<b>${aiText(String(s ?? '').replace(/\*\*/g, ''))}</b>`

/**
 * Build a design from an AI email's `content` (POST /comms/ai/generate-template
 * for an email: headline, greeting, intro, details, button, paragraphs, points,
 * closing): real heading, paragraph, columns and button blocks, each editable,
 * in the order the email reads. Text is treated as text, never as markup;
 * ${variables} pass through for the sender.
 */
export const designFromEmailContent = (content) => {
    const c = content ?? {}
    const block = (type, props) => ({
        id: newBlockId(),
        type,
        props: { ...BLOCK_TYPES[type].defaults, ...props },
    })
    const paragraph = (html, props) => block('paragraph', { text: html, ...props })
    const blocks = []

    const lines = (Array.isArray(c.headline) ? c.headline : [c.headline])
        .filter(Boolean)
        .map(aiText)
    const highlight = c.highlight ? aiText(c.highlight) : ''
    const marked = lines.map((line, i) =>
        highlight && line.includes(highlight) && !lines.slice(0, i).some((l) => l.includes(highlight))
            ? line.replace(highlight, `<font color="${EMAIL_CONTENT.highlight}">${highlight}</font>`)
            : line
    )
    if (marked.length) blocks.push(block('heading', { text: marked.join('<br/>') }))
    if (c.greeting) blocks.push(paragraph(boldText(c.greeting)))
    if (c.intro) blocks.push(paragraph(aiText(c.intro)))

    const cells = (Array.isArray(c.details) ? c.details : []).map(
        (d) =>
            `${boldText(d.label)}<br/>${aiText(d.value)}` +
            (d.note ? `<br/><small>${aiText(d.note)}</small>` : '')
    )
    for (let i = 0; i < cells.length; i += 2)
        blocks.push(block('columns', { leftText: cells[i], rightText: cells[i + 1] ?? '' }))

    // Up to three actions (`buttons`); an older service sends the one `button`.
    const buttons = Array.isArray(c.buttons) ? c.buttons : c.button ? [c.button] : []
    buttons
        .filter((b) => b?.url && b?.text)
        .forEach((b) =>
            blocks.push(block('button', { text: aiText(b.text), href: b.url }))
        )
    ;(Array.isArray(c.paragraphs) ? c.paragraphs : []).forEach((p) =>
        blocks.push(paragraph(aiText(p)))
    )
    ;(Array.isArray(c.points) ? c.points : []).forEach((p) =>
        blocks.push(
            paragraph(`${p.title ? `${boldText(p.title)} ` : ''}${aiText(p.text)}`)
        )
    )
    if (c.show_support)
        blocks.push(
            paragraph(
                'Got questions? Email us at <a href="mailto:help@bhanzu.com">help@bhanzu.com</a>.'
            )
        )
    if (c.closing) blocks.push(paragraph(aiText(c.closing)))
    blocks.push(paragraph('Warm regards,<br/><b>Team Bhanzu</b>'))

    return { version: 'bhanzu-v1', body: { ...BODY_DEFAULTS }, blocks }
}

export const isBhanzuDesign = (design) =>
    !!design &&
    typeof design === 'object' &&
    design.version === 'bhanzu-v1' &&
    Array.isArray(design.blocks)

/** Seed a design from legacy HTML: single editable raw-HTML block. */
export const designFromLegacyHtml = (html) => ({
    version: 'bhanzu-v1',
    body: { ...BODY_DEFAULTS },
    blocks: [{ id: newBlockId(), type: 'rawHtml', props: { html } }],
})
